// EWM-927 — barcode search helpers. See
// docs/superpowers/specs/2026-09-10-ewm-927-barcode-search-design.md.

/**
 * Algolia attribute (array) holding each product's barcodes, as loaded by the
 * connector (EWM-986). Indexed values are pre-normalized digit strings —
 * whitespace and hyphens already stripped — matching the FE `normalizeBarcode`
 * rule, so a scanned or hand-typed barcode filters against the same digits.
 */
export const BARCODE_FACET_ATTR = 'barcodes';

export const BARCODE_MIN_DIGITS = 8; // EAN-8
export const BARCODE_MAX_DIGITS = 14; // ITF-14

/**
 * Strip all whitespace (JS `\s` covers space/tab/NBSP/Unicode spaces) and the
 * ASCII hyphen so a scanned barcode and a hand-typed one normalize identically.
 * MUST stay byte-identical to the connector's indexed-value normalization
 * (spec Part A1) or scans won't match typed values.
 */
export const normalizeBarcode = (raw: string): string => raw.replace(/[\s-]/g, '');

const BARCODE_SHAPE_RE = new RegExp(`^[0-9]{${BARCODE_MIN_DIGITS},${BARCODE_MAX_DIGITS}}$`);

/**
 * True when a normalized query looks like a barcode: pure digits, length 8–14.
 * Anything with a letter (all SKUs, product names, keywords) is not barcode-
 * shaped and takes the unchanged text-search path.
 */
export const isBarcodeShapedQuery = (normalized: string): boolean => BARCODE_SHAPE_RE.test(normalized);

/**
 * Feature flag. Off by default; inlined at build time (a change needs a deploy).
 * Keep the barcode path inert until the connector populates BARCODE_FACET_ATTR
 * for the env (spec Part A4).
 */
export const isBarcodeSearchEnabled = (): boolean => process.env.NEXT_PUBLIC_ENABLE_BARCODE_SEARCH === 'true';
